/**
 * Black Friday → History — /black-friday/history
 *
 * Every brand × every season in one table, newest season on the right.
 * Per cell: status, what we ran, the stock-order deal, top-up. Arrows show
 * what moved against the previous season (minimum order up, extra % down…),
 * which is the first thing to know before a negotiation.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  HistoryRow,
  fmtDay,
  fmtEurShort,
  fmtPct,
  num,
  tierLine,
  useBFHistory,
} from '../../lib/api/blackFriday';
import { BrandDot, StatusPill } from './BFRegister';
import { BF_STYLES } from './styles';

const HISTORY_STYLES = `
.bf .hist-wrap { overflow: auto; max-height: calc(100vh - 220px); }
.bf .hist td, .bf .hist th { min-width: 210px; }
.bf .hist th:first-child, .bf .hist td:first-child { position: sticky; left: 0; background: var(--bg-elevated); z-index: 2; min-width: 200px; }
.bf .hist th:first-child { z-index: 3; }
.bf .cell { display: grid; gap: 3px; font-size: 12px; line-height: 1.4; cursor: pointer; border-radius: 6px; padding: 2px 4px; margin: -2px -4px; }
.bf .cell:hover { background: var(--bg-subtle); }
.bf .cell .l { color: var(--ink-2); }
.bf .cell .l b { font-weight: 600; color: var(--ink); }
.bf .up { color: #1F6B3A; font-weight: 700; }
.bf .down { color: #9A2B22; font-weight: 700; }
.bf .none { color: var(--ink-4); font-size: 12px; }
`;

type Dir = 'up' | 'down' | null;
const dir = (cur: string | null, prev: string | null | undefined): Dir => {
  const a = num(cur);
  const b = num(prev ?? null);
  if (a === null || b === null || a === b) return null;
  return a > b ? 'up' : 'down';
};
const Arrow = ({ d, goodWhen }: { d: Dir; goodWhen: 'up' | 'down' }) =>
  d ? <span className={d === goodWhen ? 'up' : 'down'} title={d === 'up' ? 'Higher than the season before' : 'Lower than the season before'}>{d === 'up' ? ' ▲' : ' ▼'}</span> : null;

function Cell({ r, prev, onOpen }: { r: HistoryRow; prev?: HistoryRow; onOpen: () => void }) {
  const allowed = num(r.discount_max_pct) !== null ? `max ${fmtPct(r.discount_max_pct)}`
    : num(r.discount_recommended_pct) !== null ? `rec ${fmtPct(r.discount_recommended_pct)}`
      : r.regional_caps?.length ? r.regional_caps.map((c) => `${c.region} ${c.max_pct}%`).join(' / ') : '';
  const deal: React.ReactNode[] = [];
  if (num(r.extra_discount_pct) !== null) {
    deal.push(<span key="x">+{fmtPct(r.extra_discount_pct)}<Arrow d={dir(r.extra_discount_pct, prev?.extra_discount_pct)} goodWhen="up" /></span>);
  }
  if (num(r.minimum_order_net) !== null) {
    deal.push(<span key="m">min {fmtEurShort(r.minimum_order_net)}<Arrow d={dir(r.minimum_order_net, prev?.minimum_order_net)} goodWhen="down" /></span>);
  }
  if (r.tiers?.length) deal.push(<span key="t">{r.tiers.length === 1 ? tierLine(r.tiers[0]) : `${r.tiers.length} tiers`}</span>);
  return (
    <div className="cell" onClick={onOpen} title="Open this season">
      <div><StatusPill status={r.status} /></div>
      {(num(r.our_discount_pct) !== null || allowed) && (
        <div className="l">
          {num(r.our_discount_pct) !== null && <><b>ran {fmtPct(r.our_discount_pct)}</b><Arrow d={dir(r.our_discount_pct, prev?.our_discount_pct)} goodWhen="up" /></>}
          {num(r.our_discount_pct) !== null && allowed && ' · '}
          {allowed && <span className="muted">{allowed}</span>}
        </div>
      )}
      {deal.length > 0 ? (
        <div className="l">{deal.reduce<React.ReactNode[]>((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}</div>
      ) : r.sellin_head ? (
        <div className="l muted">{r.sellin_head.length > 80 ? `${r.sellin_head.slice(0, 79)}…` : r.sellin_head}</div>
      ) : null}
      {(num(r.order_amount) !== null || r.topup_allowed === 'yes') && (
        <div className="l muted">
          {num(r.order_amount) !== null && `ordered ${fmtEurShort(r.order_amount)}`}
          {num(r.order_amount) !== null && r.topup_allowed === 'yes' && ' · '}
          {r.topup_allowed === 'yes' && `top-up ok${r.topup_deadline ? ` by ${fmtDay(r.topup_deadline)}` : ''}`}
        </div>
      )}
    </div>
  );
}

export const BFHistory = () => {
  const navigate = useNavigate();
  const history = useBFHistory();
  const [q, setQ] = useState('');
  const [onlyOrdered, setOnlyOrdered] = useState(false);
  const [sort, setSort] = useState<'name' | 'discount' | 'ordered'>('name');

  const { years, brands } = useMemo(() => {
    const rows = history.data ?? [];
    const yearSet = new Set<number>();
    const byBrand = new Map<string, { name: string; logo: string; color: string; priority: string; cells: Map<number, HistoryRow> }>();
    rows.forEach((r) => {
      yearSet.add(r.year);
      if (!byBrand.has(r.brand)) {
        byBrand.set(r.brand, { name: r.brand_name, logo: r.brand_logo, color: r.brand_color, priority: r.brand_priority, cells: new Map() });
      }
      byBrand.get(r.brand)!.cells.set(r.year, r);
    });
    return {
      years: [...yearSet].sort((a, b) => a - b),
      brands: [...byBrand.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name)),
    };
  }, [history.data]);

  // Brands with at least one season that was actually worked on. Brands
  // that only have an untouched "To ask" row have no history to show.
  const withHistory = brands.filter(([, b]) => [...b.cells.values()].some((c) => c.status !== 'to_ask'));
  const neverAsked = brands.length - withHistory.length;

  const lastSeason = (b: (typeof brands)[number][1]) =>
    [...b.cells.values()].filter((c) => c.status !== 'to_ask').sort((x, y) => y.year - x.year)[0];
  const lastOrdered = (b: (typeof brands)[number][1]) =>
    Math.max(0, ...[...b.cells.values()].filter((c) => c.status === 'order_placed' || c.status === 'topup_placed').map((c) => c.year));
  const lastDiscount = (b: (typeof brands)[number][1]) => {
    const l = lastSeason(b);
    return l ? (num(l.our_discount_pct) ?? num(l.discount_max_pct) ?? num(l.discount_recommended_pct) ?? -1) : -1;
  };

  const shown = withHistory.filter(([, b]) => {
    if (q.trim() && !b.name.toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (onlyOrdered && !lastOrdered(b)) return false;
    return true;
  }).sort((a, b) => {
    if (sort === 'discount') return lastDiscount(b[1]) - lastDiscount(a[1]) || a[1].name.localeCompare(b[1].name);
    if (sort === 'ordered') return lastOrdered(b[1]) - lastOrdered(a[1]) || a[1].name.localeCompare(b[1].name);
    return a[1].name.localeCompare(b[1].name);
  });

  return (
    <div className="bf">
      <style>{BF_STYLES + HISTORY_STYLES}</style>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Black Friday</div>
          <h1 className="page-title">History</h1>
        </div>
      </div>
      <div className="toolbar">
        <input className="input" placeholder="Search brand…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={`filter-pill ${onlyOrdered ? 'active' : ''}`} onClick={() => setOnlyOrdered((v) => !v)}>Ordered at least once</button>
        <select className="input" style={{ maxWidth: 230 }} value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="name">Sort: brand name</option>
          <option value="discount">Sort: last discount, highest first</option>
          <option value="ordered">Sort: most recently ordered</option>
        </select>
        <span className="muted small" style={{ marginLeft: 'auto' }} title="▲▼ = changed against the season before (green is better for us)">
          {shown.length} of {brands.length} brands{neverAsked > 0 ? ` · ${neverAsked} never asked, not shown` : ''}
        </span>
      </div>
      <Card>
        {history.isLoading ? (
          <div className="empty">Loading…</div>
        ) : withHistory.length === 0 ? (
          <div className="empty">No history yet.</div>
        ) : shown.length === 0 ? (
          <div className="empty">
            No brands match{q.trim() ? <> “{q.trim()}”</> : ''}{onlyOrdered ? ' that ordered at least once' : ''}.{' '}
            <button type="button" className="link small" onClick={() => { setQ(''); setOnlyOrdered(false); }}>Clear filters</button>
          </div>
        ) : (
          <div className="hist-wrap">
            <table className="hist">
              <thead>
                <tr>
                  <th>Brand</th>
                  {years.map((y) => <th key={y}>{y}</th>)}
                </tr>
              </thead>
              <tbody>
                {shown.map(([id, b]) => (
                  <tr key={id}>
                    <td>
                      <div className="brand-cell">
                        <BrandDot name={b.name} logo={b.logo} color={b.color} />
                        <span className="brand-name">{b.name}</span>
                      </div>
                    </td>
                    {years.map((y) => {
                      const r = b.cells.get(y);
                      return (
                        <td key={y}>
                          {r ? (
                            <Cell r={r} prev={b.cells.get(y - 1)} onOpen={() => navigate(`/black-friday/${y}/${id}`)} />
                          ) : (
                            <span className="none">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BFHistory;
